import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import i18n from "@/i18n";

const AuthContext = createContext({ user: null, loading: true, refresh: () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Sync backend-preferred language into i18n (once per session, if it differs)
      if (data?.language && data.language !== i18n.language) {
        i18n.changeLanguage(data.language);
        document.documentElement.setAttribute("lang", data.language);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {/* ignore */}
    setUser(null);
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip /me check - AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
